import { useEffect, useState } from "preact/hooks";
import { adminApi, type AdminUser, type AuditEntry } from "./api";
import { Link } from "./app";
import { STORAGE_WARN_BYTES } from "./dashboard";
import { formatAgo, formatBytes, formatCount, formatDateTime } from "./format";

function mailboxSummary(u: AdminUser): { label: string; tone: string } {
  if (u.status === "disabled") return { label: "Disabled", tone: "danger" };
  if (!u.credentials) return { label: "No password", tone: "" };
  if (u.credentials.needsPassword) return { label: "Needs password", tone: "danger" };
  const m = u.mailbox;
  if (!m) return { label: "Unknown", tone: "warn" };
  if (m.state === "wiping") return { label: "Deleting", tone: "danger" };
  if (m.state === "paused") return { label: "Paused", tone: "warn" };
  if (m.lastError) return { label: "Retrying", tone: "warn" };
  if (m.folders.some((f) => !f.backfillDone)) return { label: "Indexing", tone: "" };
  return { label: "Up to date", tone: "ok" };
}

export function AdminView({ selfEmail }: { selfEmail: string }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [deleting, setDeleting] = useState<AdminUser | null>(null);
  const [typed, setTyped] = useState("");

  const load = async () => {
    try {
      const [u, a] = await Promise.all([adminApi.users(), adminApi.audit(50)]);
      setUsers(u.users);
      setAudit(a.entries);
      setError("");
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    void load();
    const t = setInterval(() => document.visibilityState === "visible" && void load(), 10_000);
    return () => clearInterval(t);
  }, []);

  const act = async (u: AdminUser, action: "reconcile" | "rebuild" | "disable" | "enable") => {
    const prompts: Record<string, string> = {
      rebuild: `Throw away ${u.email}'s index and rebuild it from the mail server? Search for them is incomplete until it finishes.`,
      disable: `Disable ${u.email}? Their sync pauses and every ChatGPT/Claude connection they made is revoked.`,
    };
    if (prompts[action] && !confirm(prompts[action])) return;
    setBusy(`${u.id}:${action}`);
    try {
      await adminApi.act(u.id, action);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(`${deleting.id}:delete`);
    try {
      await adminApi.remove(deleting.id, typed);
      setDeleting(null);
      setTyped("");
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy("");
    }
  };

  return (
    <main>
      <p>
        <Link href="/">← Back</Link>
      </p>
      <section class="card">
        <div class="row spread">
          <h2>Users</h2>
          <button onClick={() => void load()}>Refresh</button>
        </div>
        <p class="sub">Everyone who has signed in. Refreshes every 10 seconds.</p>
        {error && <p class="notice danger">{error}</p>}
        {!users && !error && <p class="muted">Loading…</p>}
        {users && (
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Mailbox</th>
                  <th class="num">Indexed</th>
                  <th class="num hide-sm">Storage</th>
                  <th class="hide-sm">Last sync</th>
                  <th class="num hide-sm">Connections</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const s = mailboxSummary(u);
                  const m = u.mailbox;
                  const self = u.email === selfEmail;
                  const isBusy = busy.startsWith(`${u.id}:`);
                  return (
                    <tr key={u.id}>
                      <td>
                        <div>{u.email}</div>
                        <div class="muted small">
                          {u.username}
                          {u.isAdmin && " · admin"} · last sign-in {formatAgo(u.lastLoginAt ? new Date(u.lastLoginAt).toISOString() : null)}
                        </div>
                      </td>
                      <td>
                        <span class={`badge ${s.tone}`}>{s.label}</span>
                        {m?.lastError && <div class="small error-text">{m.lastError}</div>}
                      </td>
                      <td class="num">
                        {m ? `${formatCount(m.totals.indexed)} / ${formatCount(m.totals.serverMessages)}` : "—"}
                        {m && <div class="muted small">{m.totals.percent}%</div>}
                      </td>
                      <td class={`num hide-sm${m && m.footprint.databaseBytes > STORAGE_WARN_BYTES ? " error-text" : ""}`}>
                        {m ? formatBytes(m.footprint.databaseBytes + m.footprint.rawMailBytes) : "—"}
                        {m && <div class="muted small">{formatCount(m.footprint.vectors)} vectors</div>}
                      </td>
                      <td class="hide-sm small">{formatAgo(m?.lastSyncAt)}</td>
                      <td class="num hide-sm">{u.grants}</td>
                      <td>
                        <div class="row actions-cell">
                          <button disabled={isBusy || !u.credentials} onClick={() => act(u, "reconcile")} title="Re-check every folder against the server">
                            Re-check
                          </button>
                          <button disabled={isBusy || !u.credentials} onClick={() => act(u, "rebuild")} title="Delete the index and build it again">
                            Rebuild
                          </button>
                          {!self &&
                            (u.status === "active" ? (
                              <button disabled={isBusy} onClick={() => act(u, "disable")}>
                                Disable
                              </button>
                            ) : (
                              <button disabled={isBusy} onClick={() => act(u, "enable")}>
                                Enable
                              </button>
                            ))}
                          {!self && (
                            <button class="danger" disabled={isBusy} onClick={() => { setDeleting(u); setTyped(""); }}>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {deleting && (
          <div class="notice danger stack" style={{ marginTop: "16px" }}>
            <strong>Delete {deleting.email} and everything stored for them?</strong>
            <span>
              This revokes their ChatGPT and Claude connections and deletes their password, index, search vectors and stored mail.
              They can sign in again later and start over.
            </span>
            <label for="confirm-delete">Type {deleting.email} to confirm</label>
            <div class="row">
              <input id="confirm-delete" type="text" style={{ flex: "1 1 260px" }} value={typed} onInput={(e) => setTyped(e.currentTarget.value)} />
              <button onClick={() => setDeleting(null)}>Cancel</button>
              <button class="danger solid" disabled={typed.trim().toLowerCase() !== deleting.email || busy !== ""} onClick={remove}>
                Delete user
              </button>
            </div>
          </div>
        )}
      </section>

      <section class="card">
        <h2>Recent activity</h2>
        <p class="sub">Sign-ins, password checks, admin actions and sync failures. Never message content.</p>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th>Event</th>
                <th class="hide-sm">Detail</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id}>
                  <td class="small">{formatDateTime(new Date(a.at).toISOString())}</td>
                  <td class="small">{a.email ?? a.user_id ?? "—"}</td>
                  <td class="small">{a.action}</td>
                  <td class="small muted hide-sm">{a.detail ?? ""}</td>
                </tr>
              ))}
              {audit.length === 0 && (
                <tr>
                  <td colSpan={4} class="muted">
                    Nothing yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
